import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, RefreshCw, Save, Sparkles, Users, IndianRupee, Wheat, ShieldCheck, Plus, Minus, FileText, Sun, Ban, Package, Utensils } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { calculateReport } from "../lib/mdmCalculator";
import { CATEGORY_DISPLAY, formatCurrency } from "../lib/mdmData";
import { exportPDF } from "../lib/mdmExport";
import PremiumOnboarding from "../components/PremiumOnboarding";

const CATEGORIES = ["balvatika", "primary", "upper_primary", "high_school"];
const DEFAULT_COUNTS = { balvatika: 0, primary: 0, upper_primary: 0, high_school: 0 };

export default function DailyEntry() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading, isAuthed } = useAuth();
  const [sub, setSub] = useState(null);
  const [date, setDate] = useState(new Date());
  const [counts, setCounts] = useState(DEFAULT_COUNTS);
  const [overrides, setOverrides] = useState({});
  const [dateOpen, setDateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [dayType, setDayType] = useState("WORKING_DAY");   // WORKING_DAY | HOLIDAY
  const [mealType, setMealType] = useState("MDM");         // MDM | DRY_RATION
  const [conflictOpen, setConflictOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState(null);   // fn to run on Replace

  useEffect(() => {
    if (!loading && !isAuthed) nav("/login");
  }, [loading, isAuthed, nav]);

  useEffect(() => {
    if (!isAuthed) return;
    api.subMe().then(setSub).catch(() => {});
    api.listRateOverrides().then((rows) => {
      const map = {};
      (rows || []).forEach((r) => { map[r.academic_year] = r.rates || {}; });
      setOverrides(map);
    }).catch(() => {});
  }, [isAuthed]);

  // Look for an existing entry when the date changes
  useEffect(() => {
    if (!isAuthed) return;
    const iso = format(date, "yyyy-MM-dd");
    api.listDailyEntries({ from_date: iso, to_date: iso }).then((rows) => {
      const meal = (rows || []).find((r) => (r.entry_type || "meal") === "meal");
      if (meal) {
        setExisting(meal);
        setCounts({ ...DEFAULT_COUNTS, ...(meal.counts || {}) });
        setDayType(meal.day_type || "WORKING_DAY");
        setMealType(meal.meal_type || "MDM");
      } else {
        setExisting(null);
      }
    }).catch(() => setExisting(null));
  }, [date, isAuthed]);

  const report = useMemo(() => {
    try {
      const r = calculateReport(date, counts, overrides);
      r.school = { name: user?.school_name || "", udise: user?.udise || "" };
      return r;
    } catch { return null; }
  }, [date, counts, overrides, user]);

  const premium = !!sub?.is_premium;
  const totalStudents = counts.balvatika + counts.primary + counts.upper_primary + counts.high_school;

  // Trigger the one-time Premium welcome + tour when we redirect from Premium.jsx
  useEffect(() => {
    if (searchParams.get("welcome") === "premium" && premium && !showWelcome) {
      setShowWelcome(true);
    }
  }, [searchParams, premium, showWelcome]);

  const dismissWelcome = () => {
    setShowWelcome(false);
    // Strip the query param so a refresh doesn't reopen the tour
    if (searchParams.get("welcome")) {
      const next = new URLSearchParams(searchParams);
      next.delete("welcome");
      setSearchParams(next, { replace: true });
    }
  };

  const setCount = (cat, val) => setCounts((prev) => ({ ...prev, [cat]: Math.max(0, Number(val) || 0) }));
  const step = (cat, delta) => setCount(cat, (counts[cat] || 0) + delta);

  const performSave = async () => {
    setBusy(true);
    try {
      const iso = format(date, "yyyy-MM-dd");
      if (dayType === "HOLIDAY") {
        // Zero-cost record — server will also enforce this.
        await api.saveDailyEntry({
          date_iso: iso,
          weekday_name: report?.weekday_name || format(date, "EEEE"),
          academic_year: report?.academic_year || "",
          menu_display: "Holiday",
          counts: { balvatika: 0, primary: 0, upper_primary: 0, high_school: 0 },
          summary: { total_students: 0, grand_total: 0, total_rice_kg: 0 },
          aggregated_items: {},
          categories: {},
          entry_type: "meal",
          day_type: "HOLIDAY",
          meal_type: null,
        });
        toast.success(existing ? "Marked as Holiday" : "Holiday saved 🔴");
      } else {
        if (totalStudents <= 0) { setBusy(false); return toast.error("Enter at least one attendance count"); }
        if (!report) { setBusy(false); return toast.error("Nothing to save"); }
        if (!user?.udise) { setBusy(false); toast.error("Please set your school UDISE in Profile first"); return nav("/profile"); }
        await api.saveDailyEntry({
          date_iso: iso,
          weekday_name: report.weekday_name,
          academic_year: report.academic_year,
          menu_display: report.menu_display,
          counts,
          summary: report.summary,
          aggregated_items: report.aggregated_items,
          categories: report.categories,
          entry_type: "meal",
          day_type: "WORKING_DAY",
          meal_type: mealType,
        });
        toast.success(existing ? "Daily entry updated" : mealType === "DRY_RATION" ? "Dry Ration day saved 🔵" : "Daily entry saved 🟢");
      }
      const rows = await api.listDailyEntries({ from_date: iso, to_date: iso });
      const fresh = (rows || []).find((r) => (r.entry_type || "meal") === "meal");
      setExisting(fresh || null);
      if (fresh) {
        setDayType(fresh.day_type || "WORKING_DAY");
        setMealType(fresh.meal_type || "MDM");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
      setConflictOpen(false);
      setPendingSave(null);
    }
  };

  const doSave = () => {
    if (!premium) { toast("Premium needed to save entries"); return nav("/premium"); }
    // Working-Day guards happen inside performSave (after the confirm)
    if (dayType === "WORKING_DAY") {
      if (totalStudents <= 0) return toast.error("Enter at least one attendance count");
      if (!user?.udise) { toast.error("Please set your school UDISE in Profile first"); return nav("/profile"); }
    }
    // Conflict? existing entry has different classification → confirm dialog
    const existingKey = existing ? `${existing.day_type || "WORKING_DAY"}:${existing.meal_type || ""}` : null;
    const newKey = `${dayType}:${dayType === "HOLIDAY" ? "" : mealType}`;
    if (existing && existingKey !== newKey) {
      setPendingSave(() => performSave);
      setConflictOpen(true);
      return;
    }
    performSave();
  };

  const doPDF = async () => {
    if (!report) return;
    try {
      await exportPDF(report);
      toast.success("PDF downloaded");
    } catch { toast.error("PDF failed"); }
  };

  if (loading || !user) return <div className="p-10 text-center text-slate-500">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="daily-entry-page">
      <PremiumOnboarding show={showWelcome} onDismiss={dismissWelcome}/>
      <div>
        <div className="chip chip-green"><ShieldCheck size={14}/> Daily entry</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Save today's report</h1>
        <p className="mt-1 text-slate-600">Enter attendance — we compute the day using the same PM POSHAN engine and save it to your register.</p>
        {existing && (
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="chip chip-slate" data-testid="daily-existing-badge">
              📒 Existing entry: {existing.day_type === "HOLIDAY" ? "🔴 Holiday" : existing.meal_type === "DRY_RATION" ? "🔵 Dry Ration" : "🟢 MDM"} — saving will overwrite.
            </span>
          </div>
        )}
      </div>

      {!premium && (
        <div className="rounded-2xl border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50 p-5 flex items-start gap-3" data-testid="daily-paywall">
          <Sparkles size={18} className="text-orange-600 mt-0.5 shrink-0"/>
          <div className="flex-1">
            <div className="font-display font-bold text-orange-900">Premium needed to save · ₹299/year (Launch Offer)</div>
            <p className="text-sm text-orange-900/80">You can preview the calculation below, but saving to your register requires Premium.</p>
          </div>
          <Button onClick={() => nav("/premium")} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="daily-upgrade-btn">Unlock →</Button>
        </div>
      )}

      {/* Date + School */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">Date</Label>
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full h-12 mt-1.5 justify-start text-left font-semibold" data-testid="daily-date-btn">
                  <CalendarIcon size={16}/> {format(date, "EEEE, dd MMM yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={date} onSelect={(d) => { if (d) { setDate(d); setDateOpen(false); } }}
                          fromYear={2020} toYear={2100} captionLayout="dropdown-buttons"/>
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">School</Label>
            <div className="mt-1.5 h-12 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center text-sm" data-testid="daily-school-info">
              {user.school_name || "—"} · UDISE {user.udise || "unset"}
            </div>
          </div>
        </div>

        {/* Day Type toggle */}
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">Day type</Label>
          <div className="mt-2 grid grid-cols-2 gap-2" data-testid="daily-daytype">
            <button type="button" onClick={() => setDayType("WORKING_DAY")}
              className={`h-14 rounded-xl border-2 font-semibold flex items-center justify-center gap-2 transition-all ${dayType === "WORKING_DAY" ? "border-emerald-600 bg-emerald-50 text-emerald-900 shadow-sm" : "border-slate-200 bg-white text-slate-600 hover:border-emerald-200"}`}
              data-testid="daily-daytype-working">
              <Sun size={18}/> 🟢 Working Day
            </button>
            <button type="button" onClick={() => setDayType("HOLIDAY")}
              className={`h-14 rounded-xl border-2 font-semibold flex items-center justify-center gap-2 transition-all ${dayType === "HOLIDAY" ? "border-red-500 bg-red-50 text-red-900 shadow-sm" : "border-slate-200 bg-white text-slate-600 hover:border-red-200"}`}
              data-testid="daily-daytype-holiday">
              <Ban size={18}/> 🔴 Holiday
            </button>
          </div>
        </div>

        {/* Meal Type toggle — only for working days */}
        {dayType === "WORKING_DAY" && (
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">Meal type</Label>
            <div className="mt-2 grid grid-cols-2 gap-2" data-testid="daily-mealtype">
              <button type="button" onClick={() => setMealType("MDM")}
                className={`h-14 rounded-xl border-2 font-semibold flex items-center justify-center gap-2 transition-all ${mealType === "MDM" ? "border-amber-500 bg-amber-50 text-amber-900 shadow-sm" : "border-slate-200 bg-white text-slate-600 hover:border-amber-200"}`}
                data-testid="daily-mealtype-mdm">
                <Utensils size={18}/> 🟠 Mid-Day-Meal
              </button>
              <button type="button" onClick={() => setMealType("DRY_RATION")}
                className={`h-14 rounded-xl border-2 font-semibold flex items-center justify-center gap-2 transition-all ${mealType === "DRY_RATION" ? "border-sky-500 bg-sky-50 text-sky-900 shadow-sm" : "border-slate-200 bg-white text-slate-600 hover:border-sky-200"}`}
                data-testid="daily-mealtype-dry">
                <Package size={18}/> 🔵 Dry Ration
              </button>
            </div>
            {mealType === "DRY_RATION" && (
              <p className="mt-2 text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
                <b>Dry Ration Day.</b> Attendance is captured and rice/cost is computed using the current PM POSHAN rates until an admin configures specific Dry Ration rates.
              </p>
            )}
          </div>
        )}

        {/* Attendance — hidden on Holiday */}
        {dayType === "WORKING_DAY" && (
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">Attendance</Label>
            <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {CATEGORIES.map((cat) => {
                const meta = CATEGORY_DISPLAY[cat];
                return (
                  <div key={cat} className="rounded-xl border border-slate-200 bg-slate-50/40 p-3" data-testid={`daily-cat-${cat}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{meta.emoji}</span>
                      <span className="font-semibold text-slate-900 text-sm">{meta.name}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-1">
                      <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => step(cat, -1)} data-testid={`daily-dec-${cat}`}><Minus size={14}/></Button>
                      <Input type="number" min="0" value={counts[cat]} onChange={(e) => setCount(cat, e.target.value)}
                             className="h-10 text-center font-display text-lg font-bold" data-testid={`daily-count-${cat}`}/>
                      <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => step(cat, 1)} data-testid={`daily-inc-${cat}`}><Plus size={14}/></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {dayType === "HOLIDAY" && (
          <div className="rounded-xl border-2 border-red-200 bg-red-50 p-5 text-center" data-testid="daily-holiday-notice">
            <Ban size={28} className="text-red-600 mx-auto"/>
            <p className="mt-2 font-display font-extrabold text-red-900">🔴 Holiday · {format(date, "dd MMM yyyy")}</p>
            <p className="mt-1 text-sm text-red-800">No MDM / Dry Ration entry required for this date. Cost will be recorded as ₹0 in reports.</p>
          </div>
        )}
      </div>

      {/* Preview (WORKING_DAY + MDM/Dry Ration) */}
      {dayType === "WORKING_DAY" && report && (
        <div className="rounded-2xl border border-emerald-200 bg-white p-5" data-testid="daily-preview">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <PreviewKpi icon={<Users size={16}/>} label="Students" value={report.summary.total_students}/>
            <PreviewKpi icon={<IndianRupee size={16}/>} label="Total cost" value={formatCurrency(report.summary.grand_total)}/>
            <PreviewKpi icon={<Wheat size={16}/>} label="Rice" value={`${report.summary.total_rice_kg.toFixed(2)} kg`}/>
            <PreviewKpi icon={<CalendarIcon size={16}/>} label={mealType === "DRY_RATION" ? "Type" : "Menu"} value={<span className="text-sm truncate block">{mealType === "DRY_RATION" ? "Dry Ration" : report.menu_display}</span>}/>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 justify-end">
            <Button onClick={doPDF} variant="outline" data-testid="daily-pdf-btn"><FileText size={14}/> Download PDF</Button>
            <Button onClick={doSave} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="daily-save-btn">
              <Save size={14}/> {busy ? "Saving…" : existing ? "Replace entry" : "Save daily entry"}
            </Button>
          </div>
        </div>
      )}

      {dayType === "HOLIDAY" && (
        <div className="flex justify-end">
          <Button onClick={doSave} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="daily-save-btn">
            <Save size={14}/> {busy ? "Saving…" : existing ? "Replace as Holiday" : "Save Holiday"}
          </Button>
        </div>
      )}

      {/* Conflict dialog */}
      <Dialog open={conflictOpen} onOpenChange={(v) => { if (!v) { setConflictOpen(false); setPendingSave(null); } }}>
        <DialogContent data-testid="daily-conflict-dialog">
          <DialogHeader>
            <DialogTitle>An entry already exists for this date</DialogTitle>
            <DialogDescription>
              {format(date, "dd MMM yyyy")} is currently saved as{" "}
              <b>{existing?.day_type === "HOLIDAY" ? "🔴 Holiday" : existing?.meal_type === "DRY_RATION" ? "🔵 Dry Ration" : "🟢 MDM"}</b>.
              You're about to replace it with{" "}
              <b>{dayType === "HOLIDAY" ? "🔴 Holiday" : mealType === "DRY_RATION" ? "🔵 Dry Ration" : "🟢 MDM"}</b>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => { setConflictOpen(false); setPendingSave(null); nav("/history"); }} data-testid="daily-conflict-view">View entry</Button>
            <Button variant="outline" onClick={() => { setConflictOpen(false); setPendingSave(null); }} data-testid="daily-conflict-cancel">Cancel</Button>
            <Button onClick={() => pendingSave && pendingSave()} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="daily-conflict-replace">Replace</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PreviewKpi({ icon, label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">{icon}{label}</div>
      <div className="mt-1 font-display font-extrabold text-lg text-slate-900">{value}</div>
    </div>
  );
}
